import React, { createElement, useEffect, useState, useMemo, useCallback } from 'react';
import { Table as OriginalTable, Form, Field, Dialog, Button, ResponsiveGrid } from '@alifd/next';
import { nanoid } from 'nanoid';
import { renderFormItem } from '@/components/table/pro-table/edit-table/components/edit-from/render-form-block-component';
import { cloneDeep, isEmpty } from 'lodash-es';
const ServiceTable = (props) => {
    const { children = [], __designMode, field, listName, formProps, getHandler, changingField, getSelectRow, editType } = props;
    const { columns, spacing, primaryKey = 'uid', status, emptyContent } = formProps;
    const { onChange: onTableFieldChange, value = [] } = field?.init(listName);
    const isPreview = status === 'readonly' || editType === 'view';
    const isDesign = __designMode === 'design';
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const dataSource = useMemo(() => value?.map((item) => ({ ...item, uid: item.uid ?? nanoid() } || [])), [value]);
    const formField = Field.useField({
        parseName: true,
    });
    const reset = useCallback(() => {
        formField?.reset();
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [formField]);
    useEffect(() => {
        getHandler({ reset });
    }, [formField, getHandler, reset]);
    const onRowChange = useCallback((selectedRowKeys, records) => {
        formField.reset();
        setSelectedRowKeys(selectedRowKeys);
        setSelectedRows(records);
        formField.setValues(records[0]);
        getSelectRow(records);
    }, [formField, getSelectRow]);
    const save = () => {
        formField.validate((errors, values) => {
            if (!errors) {
                const currentPrimaryKey = selectedRows?.[0]?.uid;
                if (currentPrimaryKey) {
                    const newData = dataSource.map((item) => {
                        if (item.uid === currentPrimaryKey) {
                            const { serviceId = '', uid = '' } = values || {};
                            return { serviceId, uid };
                        }
                        return item;
                    });
                    onTableFieldChange(newData);
                    resetSelectedRows();
                    formField.reset();
                }
            }
        });
    };
    const resetSelectedRows = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };
    const cancle = () => {
        resetSelectedRows();
        formField.reset();
    };
    const rowSelection = {
        mode: 'single',
        onChange: onRowChange,
        selectedRowKeys,
        getProps: () => {
            return {
                disabled: isDesign || status === 'readonly',
            };
        },
    };
    const onDelete = React.useCallback((index) => {
        Dialog.confirm({
            title: '确认删除吗',
            closeMode: ['close', 'esc', 'mask'],
            onOk: () => {
                const newData = dataSource;
                newData.splice(index, 1);
                onTableFieldChange(newData);
                resetSelectedRows();
            },
        });
    }, [dataSource, onTableFieldChange]);
    const Operation = React.useCallback(() => {
        const index = dataSource?.findIndex((d) => d?.uid === selectedRowKeys[0]);
        const onAdd = () => {
            const newData = isEmpty(dataSource) ? [] : cloneDeep(dataSource);
            newData?.push({ uid: nanoid() });
            onTableFieldChange(newData);
        };
        if (isPreview) {
            return null;
        }
        return (createElement("span", null,
            createElement(Button, { type: "primary", onClick: onAdd, disabled: isDesign }, "\u65B0\u589E"),
            createElement(Button, { warning: true, style: { marginLeft: 4 }, onClick: () => onDelete(index), disabled: isDesign || selectedRowKeys.length === 0 }, "\u5220\u9664")));
    }, [dataSource, isDesign, onDelete, onTableFieldChange, primaryKey, selectedRowKeys, isPreview]);
    return (createElement("div", null,
        createElement("div", { style: { marginBottom: 4 } },
            createElement(Operation, null)),
        createElement(OriginalTable.StickyLock, { dataSource: dataSource, primaryKey: "uid", rowSelection: rowSelection, size: 'small', fixedHeader: true, isZebra: true, crossline: true, maxBodyHeight: 224 }, formatTableColumn(children)),
        (isDesign || selectedRows.length > 0) && !isPreview && (createElement(Form, { isPreview: isPreview, field: formField, useLabelForErrorMessage: true },
            createElement(ResponsiveGrid, { gap: spacing, columns: columns }, children ? (formatFormItems(children, { ...formProps, changingField, field })) : (createElement("div", { className: "empty-content" }, emptyContent))),
            isPreview ? null : (createElement("div", { style: { textAlign: 'center' } },
                createElement(Button.Group, null,
                    createElement(Button, { onClick: save, type: "primary", disabled: isDesign || selectedRows.length === 0 }, "\u4FEE\u6539"),
                    createElement(Button, { onClick: cancle, disabled: isDesign }, "\u53D6\u6D88"))))))));
};
export default ServiceTable;
const formatTableColumn = (columns) => {
    return columns
        ?.filter((col) => col?.isShowInColumn)
        ?.map((item) => {
        const { label, name, type, dictData = [] } = item;
        const columnsProps = { title: label, dataIndex: name, width: 200 };
        if (type === 'dict') {
            return (createElement(OriginalTable.Column, { key: columnsProps.dataIndex, ...columnsProps, cell: (val) => {
                    const { label: renderLabel } = dictData.filter(eve => eve.value === val)[0] || {};
                    return (createElement("span", null, renderLabel));
                } }));
        }
        return createElement(OriginalTable.Column, { key: columnsProps.dataIndex, ...columnsProps });
    });
};
const formatFormItems = (columns, globalProps) => {
    const { labelAlign, labelCol, wrapperCol, labelTextAlign, changingField, field } = globalProps;
    return columns.map((item) => {
        return (createElement(ResponsiveGrid.Cell, { colSpan: item.columnSpan }, renderFormItem(item, {
            labelAlign,
            labelCol,
            wrapperCol,
            labelTextAlign,
            changingField,
            field,
        })));
    });
};
